import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { useMonthlyRevenue } from '../../../hooks/useMonthlyRevenue';
import {
  getAvailableYears,
  filterByYear,
  computeKPIs,
  buildPerformanceTiles,
  fmtMonth,
} from '../../../utils/monthlyRevenueUtils';
import { KpiCards } from './KpiCards';
import { RevenueTrendChart } from './RevenueTrendChart';
import { MomChangeChart } from './MomChangeChart';
import { PerformanceTiles } from './PerformanceTiles';
import { appPath } from '../../../utils/appPath';

// ─── Skeletons ─────────────────────────────────────────────────────────────────
const SkeletonCard: React.FC<{ h?: string }> = ({ h = 'h-32' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-md ${h} animate-pulse`} />
);

const linkToSeasonality = (
  <Link
    to={appPath('/revenue-seasonality')}
    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
  >
    Annual revenue seasonality (multi-FY) →
  </Link>
);

const LoadingSkeleton: React.FC = () => (
  <div className="p-6 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SkeletonCard h="h-44" />
      <SkeletonCard h="h-44" />
    </div>
    <SkeletonCard h="h-80" />
    <SkeletonCard h="h-64" />
    <SkeletonCard h="h-32" />
  </div>
);

// ─── Filter select ─────────────────────────────────────────────────────────────
const FilterSelect: React.FC<{
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">{label}</span>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

// ─── Executive Summary ─────────────────────────────────────────────────────────
const ExecutiveSummary: React.FC<{
  latestRecord: NonNullable<ReturnType<typeof computeKPIs>['latestRecord']>;
  kpis: ReturnType<typeof computeKPIs>;
}> = ({ latestRecord, kpis }) => {
  const momDir = latestRecord.mom_change_percent >= 0 ? 'increased' : 'decreased';
  const yoyDir = latestRecord.yoy_change_percent >= 0 ? 'up' : 'down';
  const positionDesc =
    latestRecord.trend_position === 'ABOVE_TREND' ? 'above the 6-month trend line' :
    latestRecord.trend_position === 'BELOW_TREND' ? 'below the 6-month trend line' :
    'in line with the 6-month trend';

  const bullets = [
    `Revenue ${momDir} by ${Math.abs(latestRecord.mom_change_percent).toFixed(1)}% month-over-month in ${fmtMonth(latestRecord.invoice_month)}.`,
    `Year-over-year revenue is ${yoyDir} ${Math.abs(latestRecord.yoy_change_percent).toFixed(1)}% compared to the same period last year.`,
    `Current period is ${positionDesc} with ${latestRecord.volatility_level.replace(/_/g, ' ').toLowerCase()}.`,
    `The 6-month moving average stands at $${(latestRecord.six_month_avg / 1000).toFixed(1)}K, reflecting overall revenue momentum.`,
    `${latestRecord.global_unique_clients} unique clients invoiced across ${latestRecord.currencies_used} currency${latestRecord.currencies_used !== 1 ? 'ies' : 'y'} in ${fmtMonth(latestRecord.invoice_month)}.`,
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-0.5 h-4 rounded-full bg-slate-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Executive Summary</span>
      </div>
      <ul className="space-y-2">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-none mt-1.5" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
export const RevenueTrendsPage: React.FC = () => {
  const { records, loading, error } = useMonthlyRevenue();
  const [selectedYear, setSelectedYear] = useState<string>('all');

  const availableYears = useMemo(() => getAvailableYears(records), [records]);
  const filtered = useMemo(
    () => filterByYear(records, selectedYear === 'all' ? 'all' : parseInt(selectedYear, 10)),
    [records, selectedYear]
  );
  const kpis = useMemo(() => computeKPIs(filtered), [filtered]);
  const perfTiles = useMemo(() => buildPerformanceTiles(kpis, records), [kpis, records]);

  const filterBar = (
    <FilterSelect
      label="Year"
      value={selectedYear}
      onChange={setSelectedYear}
      options={[
        { value: 'all', label: 'All Years' },
        ...availableYears.map(y => ({ value: y, label: String(y) })),
      ]}
    />
  );

  if (error) {
    return (
      <AppLayout>
        <PageHeader
          title="Revenue Trends"
          underTitle={linkToSeasonality}
          filters={filterBar}
        />
        <div className="p-6 flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-1">Failed to load data</p>
            <p className="text-slate-600 text-sm">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Revenue Trends"
        underTitle={linkToSeasonality}
        filters={filterBar}
      />

      {loading ? (
        <LoadingSkeleton />
      ) : kpis.latestRecord == null ? (
        <div className="p-6 text-center text-slate-500">
          No data available for selected year.
        </div>
      ) : (
        <div className="p-6 space-y-4">
          {/* MoM & YoY KPI cards */}
          <KpiCards
            latestRecord={kpis.latestRecord}
            prevRecord={kpis.prevRecord}
            currentYearAvg={kpis.currentYearAvg}
            prevYearAvg={kpis.prevYearAvg}
            yoyAvgChange={kpis.yoyAvgChange}
          />

          {/* Revenue trend area chart */}
          <RevenueTrendChart records={filtered} />

          {/* MoM change bar chart */}
          <MomChangeChart records={filtered} />

          {/* Performance dashboard tiles */}
          <PerformanceTiles tiles={perfTiles} />

          {/* Executive summary */}
          <ExecutiveSummary latestRecord={kpis.latestRecord} kpis={kpis} />
        </div>
      )}
    </AppLayout>
  );
};

export default RevenueTrendsPage;
